import { useModal } from '@ebay/nice-modal-react';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import Status from 'components/field/Status';
import TextArea from 'components/field/TextArea';
import FormItem from 'components/form/FormItem';
import NoRecordsFound from 'components/global/NoRecordsFound';
import Typography from 'components/global/Typography';
import { ModalActions } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import { usePrompt } from 'hooks/usePrompt';

import { ORDERS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';

const OrderComment = ({ ID, formMode }) => {
	const axiosPrivate = useAxiosPrivate();
	const modal = useModal();
	const dispatch = useDispatch();
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);

	const { showSuccess, showError } = useAlert();
	const [value, setValue] = useState(null);
	const isDirty = value !== null;

	const handleCloseModal = () => {
		modal.remove();
	};

	usePrompt(isDirty && !isLoading);

	const fillForm = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(ORDERS.GET_ORDER_COMMENTS_BY_ID, { otoOrderID: ID });

			if (response.status === 200) {
				setData(response.data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (ID) {
			fillForm();
		}
	}, []);

	const onSubmit = async () => {
		setIsLoading(true);

		await axiosPrivate
			.post(ORDER_ACTIONS.ADD_COMMENT_TO_ORDER, { otoOrderID: ID, comment: value })
			.then((response) => {
				if (response.status === 200) {
					setValue(null);
					fillForm();
					showSuccess();
					dispatch(updateDynamicListsInPage());
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		setIsLoading(false);
	};

	return (
		<>
			<OrderCommentTable isLoading={isLoading} data={data} />

			{formMode !== 'view' && (
				<FormItem label="comment">
					<TextArea value={value} setValue={setValue} />
				</FormItem>
			)}
			{formMode !== 'view' && formMode !== 'modal' && (
				<FormItem>
					<Button
						label="button.addComment"
						loadingLabel="loading"
						loading={isLoading}
						icon={faPaperPlaneTop}
						onClick={onSubmit}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
					/>
				</FormItem>
			)}
			{formMode === 'modal' && (
				<ModalActions>
					<Button label="cancel" onClick={handleCloseModal} variant="outline" />
					<Button
						label="button.addComment"
						loadingLabel="loading"
						loading={isLoading}
						icon={faPaperPlaneTop}
						onClick={onSubmit}
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
					/>
				</ModalActions>
			)}
		</>
	);
};

export default OrderComment;

const OrderCommentTable = ({ data, isLoading }) => {
	const { getLocaleDateWithTime } = useLocale();
	const columns = React.useMemo(
		() => [
			{
				header: 'date',
				accessorKey: 'commentsDate',
				size: 100,
				renderCell: ({ row }) => <Typography>{getLocaleDateWithTime(row.original.commentsDate)}</Typography>
			},
			{
				header: 'createdBy',
				accessorKey: 'createdBy',
				size: 100
			},
			{
				header: 'OrderCommentTable.comments',
				accessorKey: 'comments',
				size: 100,
				showOnMobile: true
			},
			{
				header: 'status',
				accessorKey: 'status',
				size: 150,
				renderCell: ({ row }) => <>{row.original.status ? <Status value={row.original.status} /> : ''}</>,
				showOnMobile: true
			}
		],
		[]
	);

	return (
		<Table
			columns={columns}
			data={data}
			loading={isLoading}
			noRecordsFound={
				<NoRecordsFound
					lottieName="no-comments"
					height="10rem"
					title="noComments"
					subTitle="noCommentsInThisOrder"
				/>
			}
		/>
	);
};
